import React, { useEffect, useState } from "react";
import { api, getErrorMessage } from "@/lib/api";
import { Plus, Trash2, Edit3, Save, X, Tag } from "lucide-react";
import { toast } from "sonner";
import { formatsFor } from "@/lib/format";

const FIELD_TYPES = [
  { value: "text", label: "Texte" },
  { value: "date", label: "Date" },
  { value: "number", label: "Nombre" },
  { value: "contact", label: "Contact (Bedee)" },
  { value: "societe", label: "Société (interne)" },
  { value: "entreprise", label: "Entreprise (Bedee)" },
];

const blank = { key: "", label: "", field_type: "text", format: "", description: "" };

export default function MetadataCatalogPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);

  const load = () =>
    api.get("/metadata-catalog").then((r) => setItems(r.data)).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const startNew = () => { setForm(blank); setEditing(null); setShowForm(true); };
  const startEdit = (it) => {
    setForm({
      key: it.key, label: it.label, field_type: it.field_type,
      format: it.format || "", description: it.description || "",
    });
    setEditing(it.id);
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        label: form.label, field_type: form.field_type,
        format: form.format || null, description: form.description,
      };
      if (editing) {
        await api.put(`/metadata-catalog/${editing}`, payload);
        toast.success("Métadonnée mise à jour.");
      } else {
        await api.post("/metadata-catalog", { ...payload, key: form.key });
        toast.success("Métadonnée créée.");
      }
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Supprimer "${it.label}" ?`)) return;
    try {
      await api.delete(`/metadata-catalog/${it.id}`);
      toast.success("Supprimée.");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-6">
        <div className="text-sm text-zinc-600 max-w-2xl">
          Catalogue central des <strong className="text-zinc-900">métadonnées réutilisables</strong>.
          Ces champs sont ensuite rattachés à un ou plusieurs types de documents via un menu cherchable.
          Modifier une métadonnée met à jour tous les types qui l'utilisent.
        </div>
        <button
          data-testid="meta-new-btn"
          onClick={startNew}
          className="inline-flex items-center gap-2 bg-[#002FA7] hover:bg-[#00227A] text-white text-sm font-medium px-4 py-2 transition-colors shrink-0"
        >
          <Plus size={14} strokeWidth={1.5} /> Nouvelle métadonnée
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="border border-zinc-900 bg-white" data-testid="meta-form">
          <div className="p-4 border-b border-zinc-900 bg-zinc-900 text-white flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.2em] font-mono">
              {editing ? "Édition" : "Nouvelle métadonnée"}
            </div>
            <button type="button" onClick={() => setShowForm(false)} className="text-white/70 hover:text-white p-1">
              <X size={16} />
            </button>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block">
              <span className="block text-[11px] uppercase tracking-[0.15em] text-zinc-500 font-mono mb-2">
                Clé {editing && <span className="text-zinc-400 normal-case">(non modifiable)</span>}
              </span>
              <input
                data-testid="meta-key-input"
                required maxLength={80}
                value={form.key}
                disabled={!!editing}
                onChange={(e) => setForm({ ...form, key: e.target.value.toLowerCase().replace(/\s/g, "_") })}
                placeholder="ex: numero_contrat"
                className="w-full border border-zinc-300 px-4 py-2 text-sm font-mono focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] outline-none disabled:bg-zinc-50 disabled:text-zinc-400"
              />
            </label>
            <label className="block">
              <span className="block text-[11px] uppercase tracking-[0.15em] text-zinc-500 font-mono mb-2">
                Libellé
              </span>
              <input
                data-testid="meta-label-input"
                required maxLength={200}
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="ex: Numéro de contrat"
                className="w-full border border-zinc-300 px-4 py-2 text-sm focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] outline-none"
              />
            </label>
            <label className="block">
              <span className="block text-[11px] uppercase tracking-[0.15em] text-zinc-500 font-mono mb-2">
                Type
              </span>
              <select
                data-testid="meta-type-select"
                value={form.field_type}
                onChange={(e) => setForm({ ...form, field_type: e.target.value, format: "" })}
                className="w-full border border-zinc-300 px-4 py-2 text-sm focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] outline-none"
              >
                {FIELD_TYPES.map((ft) => <option key={ft.value} value={ft.value}>{ft.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="block text-[11px] uppercase tracking-[0.15em] text-zinc-500 font-mono mb-2">
                Format {formatsFor(form.field_type).length === 0 && <span className="text-zinc-400 normal-case">(non applicable)</span>}
              </span>
              <select
                data-testid="meta-format-select"
                value={form.format}
                onChange={(e) => setForm({ ...form, format: e.target.value })}
                disabled={formatsFor(form.field_type).length === 0}
                className="w-full border border-zinc-300 px-4 py-2 text-sm focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] outline-none disabled:bg-zinc-50 disabled:text-zinc-400"
              >
                <option value="">— défaut —</option>
                {formatsFor(form.field_type).map((f) => (
                  <option key={f.value || "_free"} value={f.value}>{f.label}</option>
                ))}
              </select>
            </label>
            <label className="block md:col-span-2">
              <span className="block text-[11px] uppercase tracking-[0.15em] text-zinc-500 font-mono mb-2">
                Description (optionnelle)
              </span>
              <input
                data-testid="meta-desc-input"
                maxLength={500}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full border border-zinc-300 px-4 py-2 text-sm focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] outline-none"
              />
            </label>
          </div>
          <div className="p-4 border-t border-zinc-200 bg-zinc-50/50 flex justify-end gap-2">
            <button type="button" onClick={() => setShowForm(false)} className="border border-zinc-300 text-sm px-4 py-2 hover:bg-zinc-100">
              Annuler
            </button>
            <button
              data-testid="meta-submit"
              type="submit"
              className="inline-flex items-center gap-2 bg-[#002FA7] hover:bg-[#00227A] text-white text-sm font-medium px-4 py-2"
            >
              <Save size={14} /> Enregistrer
            </button>
          </div>
        </form>
      )}

      <div className="border border-zinc-200 bg-white" data-testid="meta-list">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50/50">
              <th className="py-3 px-4 text-[10px] font-medium text-zinc-500 uppercase tracking-wider">Libellé</th>
              <th className="py-3 px-4 text-[10px] font-medium text-zinc-500 uppercase tracking-wider">Clé</th>
              <th className="py-3 px-4 text-[10px] font-medium text-zinc-500 uppercase tracking-wider">Type</th>
              <th className="py-3 px-4 text-[10px] font-medium text-zinc-500 uppercase tracking-wider">Format</th>
              <th className="py-3 px-4 text-[10px] font-medium text-zinc-500 uppercase tracking-wider">Utilisation</th>
              <th className="py-3 px-4 text-[10px] font-medium text-zinc-500 uppercase tracking-wider">Description</th>
              <th className="w-20"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="p-6 text-sm text-zinc-500">Chargement…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={7} className="p-12 text-center">
                <Tag size={32} strokeWidth={1} className="text-zinc-300 mx-auto" />
                <div className="mt-3 text-sm text-zinc-500">Aucune métadonnée — créez-en une.</div>
              </td></tr>
            ) : items.map((it) => (
              <tr key={it.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50" data-testid={`meta-row-${it.id}`}>
                <td className="py-3 px-4 text-sm font-medium">{it.label}</td>
                <td className="py-3 px-4 text-xs font-mono text-zinc-600">{it.key}</td>
                <td className="py-3 px-4">
                  <span className="inline-block text-[10px] uppercase tracking-wider font-mono px-2 py-1 border border-zinc-200 text-zinc-600">
                    {FIELD_TYPES.find((t) => t.value === it.field_type)?.label || it.field_type}
                  </span>
                </td>
                <td className="py-3 px-4 text-xs font-mono text-zinc-700">
                  {it.format || <span className="text-zinc-400">—</span>}
                </td>
                <td className="py-3 px-4 text-sm tabular-nums">
                  <span className={`font-mono ${it.usage_count > 0 ? "text-[#002FA7]" : "text-zinc-400"}`}>
                    {it.usage_count} type{it.usage_count > 1 ? "s" : ""}
                  </span>
                </td>
                <td className="py-3 px-4 text-sm text-zinc-500 truncate max-w-[280px]">{it.description || "—"}</td>
                <td className="py-3 px-4 text-right whitespace-nowrap">
                  <button onClick={() => startEdit(it)} className="text-zinc-500 hover:text-[#002FA7] p-2" data-testid={`meta-edit-${it.id}`}>
                    <Edit3 size={14} strokeWidth={1.5} />
                  </button>
                  <button onClick={() => remove(it)} className="text-zinc-500 hover:text-[#DC2626] p-2" data-testid={`meta-delete-${it.id}`}>
                    <Trash2 size={14} strokeWidth={1.5} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
